import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  Loader2,
  Bug,
  Lightbulb,
  Heart,
  MessageSquare,
  Clock,
  MailCheck,
  Plus,
  Send,
  ChevronRight,
} from "lucide-react";
import { useUserFeedback, useAddFeedbackMessage } from "@/hooks/use-feedback";
import { FeedbackDialog } from "@/components/shared/FeedbackDialog";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { FeedbackType, FeedbackStatus, UserFeedback } from "@/integrations/supabase/types";

const TYPE_CONFIG: Record<FeedbackType, { label: string; icon: typeof Bug; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  bug_report: { label: "Bug", icon: Bug, variant: "destructive" },
  feature_request: { label: "Sugestao", icon: Lightbulb, variant: "default" },
  compliment: { label: "Elogio", icon: Heart, variant: "secondary" },
  comment: { label: "Comentario", icon: MessageSquare, variant: "outline" },
};

const STATUS_CONFIG: Record<FeedbackStatus, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  new: { label: "Novo", variant: "outline" },
  in_review: { label: "Em Analise", variant: "secondary" },
  in_progress: { label: "Em Progresso", variant: "default" },
  resolved: { label: "Resolvido", variant: "default" },
  closed: { label: "Fechado", variant: "secondary" },
};

export function FeedbackSection() {
  const { data: feedbacks, isLoading } = useUserFeedback();
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const { mutate: addMessage, isPending: isSending } = useAddFeedbackMessage();

  // Derive selectedFeedback from query data so it updates reactively
  const selectedFeedback = useMemo(
    () => feedbacks?.find((f) => f.id === selectedFeedbackId) ?? null,
    [feedbacks, selectedFeedbackId]
  );

  const handleOpenDetail = (item: UserFeedback) => {
    setSelectedFeedbackId(item.id);
    setSheetOpen(true);
    setReplyText("");
  };

  const handleSendReply = () => {
    if (!selectedFeedbackId || !replyText.trim()) return;
    addMessage(
      { feedbackId: selectedFeedbackId, message: replyText.trim() },
      { onSuccess: () => setReplyText("") }
    );
  };

  const selectedType = selectedFeedback ? TYPE_CONFIG[selectedFeedback.type] : null;
  const selectedStatus = selectedFeedback ? STATUS_CONFIG[selectedFeedback.status] : null;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Meu Feedback
              </CardTitle>
              <CardDescription className="mt-1.5">
                Acompanhe o status dos seus envios
              </CardDescription>
            </div>
            <Button onClick={() => setDialogOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-1.5" />
              Criar novo
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : !feedbacks || feedbacks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>Nenhum feedback enviado ainda.</p>
              <p className="text-sm mt-1">
                Clique em "Criar novo" para enviar seu primeiro feedback!
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {feedbacks.map((item) => {
                const typeConfig = TYPE_CONFIG[item.type];
                const statusConfig = STATUS_CONFIG[item.status];
                const TypeIcon = typeConfig.icon;
                const hasReply = !!item.admin_reply;

                return (
                  <button
                    key={item.id}
                    onClick={() => handleOpenDetail(item)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors text-left"
                  >
                    <TypeIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">
                          {item.title}
                        </span>
                        <Badge variant={typeConfig.variant} className="text-[10px] h-5">
                          {typeConfig.label}
                        </Badge>
                        <Badge variant={statusConfig.variant} className="text-[10px] h-5">
                          {statusConfig.label}
                        </Badge>
                        {hasReply && (
                          <Badge variant="outline" className="text-[10px] h-5 border-primary/30 text-primary">
                            <MailCheck className="h-3 w-3 mr-0.5" />
                            Respondido
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <Clock className="h-3 w-3" />
                        {format(new Date(item.created_at), "dd/MM/yyyy", { locale: ptBR })}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          {selectedFeedback && selectedType && selectedStatus && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={selectedType.variant}>{selectedType.label}</Badge>
                  <Badge variant={selectedStatus.variant}>{selectedStatus.label}</Badge>
                </div>
                <SheetTitle className="text-left">{selectedFeedback.title}</SheetTitle>
                <SheetDescription className="text-left">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {format(new Date(selectedFeedback.created_at), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR })}
                  </span>
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-4">
                {/* Description / Messages */}
                {selectedFeedback.description && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Mensagens</p>
                    <div className="text-sm whitespace-pre-wrap bg-muted/50 rounded-lg p-3">
                      {selectedFeedback.description}
                    </div>
                  </div>
                )}

                {/* Admin Reply */}
                {selectedFeedback.admin_reply && (
                  <>
                    <Separator />
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-primary uppercase tracking-wider">
                        <MailCheck className="h-3.5 w-3.5" />
                        Resposta da equipe
                      </div>
                      <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                        <p className="text-sm whitespace-pre-wrap">{selectedFeedback.admin_reply}</p>
                        {selectedFeedback.replied_at && (
                          <p className="text-[10px] text-muted-foreground mt-2">
                            {format(new Date(selectedFeedback.replied_at), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR })}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Admin Notes (if visible) */}
                {selectedFeedback.admin_notes && (
                  <>
                    <Separator />
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Notas</p>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {selectedFeedback.admin_notes}
                      </p>
                    </div>
                  </>
                )}

                {/* User Reply Box */}
                {selectedFeedback.status !== "closed" && selectedFeedback.status !== "resolved" && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Adicionar informacoes
                      </p>
                      <Textarea
                        placeholder="Descreva mais detalhes ou responda ao suporte..."
                        rows={3}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                      />
                      <Button
                        size="sm"
                        onClick={handleSendReply}
                        disabled={isSending || !replyText.trim()}
                        className="w-full"
                      >
                        {isSending ? (
                          <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4 mr-1.5" />
                        )}
                        Enviar
                      </Button>
                    </div>
                  </>
                )}

                {(selectedFeedback.status === "closed" || selectedFeedback.status === "resolved") && (
                  <>
                    <Separator />
                    <p className="text-xs text-center text-muted-foreground py-2">
                      Este feedback foi {selectedFeedback.status === "resolved" ? "resolvido" : "fechado"}.
                    </p>
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Create Feedback Dialog */}
      <FeedbackDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}
